import { useToast } from "@chakra-ui/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createUserWithProfileViaEdgeFunction,
  isUserAdmin,
  supabase,
} from "../lib/supabase";

export interface Owner {
  id: string;
  name: string | null;
  email: string | null;
  phone: string | null;
  created_at: string | null;
  property_count: number;
  email_confirmed_at: string | null;
  role: "admin" | "user";
}

export interface OwnerFormData {
  name: string;
  email: string;
  phone: string;
}

export interface OwnerFilters {
  searchTerm: string;
  sortBy: "name" | "email" | "created_at";
  page: number;
  limit: number;
  showDeleted?: boolean;
}

export function useOwnersSimple(filters?: OwnerFilters) {
  const queryClient = useQueryClient();
  const toast = useToast();

  // Check admin status
  const {
    data: isAdmin = false,
    isLoading: isCheckingAdmin,
    error: adminError,
  } = useQuery({
    queryKey: ["admin-status"],
    queryFn: isUserAdmin,
    retry: 1,
  });

  // Fetch owners query
  const {
    data: owners = [],
    isLoading: isLoadingOwners,
    error: ownersError,
    refetch,
  } = useQuery({
    queryKey: ["owners", filters?.showDeleted],
    queryFn: async (): Promise<Owner[]> => {
      // Use the fetchAllOwners function from supabase.ts
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No authentication session found");
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/get-owners`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          name: "Functions",
          limit: 1000,
          showDeleted: filters?.showDeleted || false,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to fetch owners");
      }

      const result = await response.json();
      return result.data || [];
    },
    enabled: isAdmin && !isCheckingAdmin,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Create owner mutation
  const createOwnerMutation = useMutation({
    mutationFn: async (ownerData: OwnerFormData): Promise<any> => {
      const cleanPhone = ownerData.phone
        ? ownerData.phone.replace(/\D/g, "")
        : undefined;

      const result = await createUserWithProfileViaEdgeFunction({
        email: ownerData.email,
        name: ownerData.name,
        phone: cleanPhone,
      });

      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owners"] });
      toast({
        title: "Proprietário criado com sucesso!",
        description:
          "Você pode agora enviar um convite para que ele defina sua senha.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao criar proprietário",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    },
  });

  // Update owner mutation
  const updateOwnerMutation = useMutation({
    mutationFn: async ({
      id,
      data,
    }: {
      id: string;
      data: OwnerFormData;
    }): Promise<any> => {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error("No authentication session found");
      }

      const cleanPhone = data.phone ? data.phone.replace(/\D/g, "") : undefined;

      // Prepare update data
      const updateData: any = {
        name: data.name,
        phone: cleanPhone,
      };

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(`${supabaseUrl}/functions/v1/profiles`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({
          action: "update",
          id,
          data: updateData,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to update owner");
      }

      const result = await response.json();
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["owners"] });
      toast({
        title: "Proprietário atualizado com sucesso!",
        description: "As informações do proprietário foram atualizadas.",
        status: "success",
        duration: 3000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao atualizar proprietário",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    },
  });

  // Send verification email mutation
  const sendVerificationMutation = useMutation({
    mutationFn: async (email: string) => {
      const { data, error } = await supabase.functions.invoke(
        "send-verification-email",
        {
          body: { email },
        }
      );

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      toast({
        title: "Convite enviado com sucesso!",
        description:
          "O link de convite foi gerado. O proprietário poderá definir sua senha através deste link.",
        status: "success",
        duration: 5000,
      });
    },
    onError: (error: Error) => {
      toast({
        title: "Erro ao enviar convite",
        description: error.message,
        status: "error",
        duration: 5000,
      });
    },
  });

  // Helper function to transform form data to owner data
  const transformFormDataToOwnerData = (
    formData: OwnerFormData
  ): OwnerFormData => {
    const cleanPhone = formData.phone.replace(/\D/g, "");

    return {
      name: formData.name,
      email: formData.email,
      phone: cleanPhone,
    };
  };

  // Helper function to transform owner data to form data
  const transformOwnerToFormData = (owner: Owner): OwnerFormData => {
    return {
      name: owner.name || "",
      email: owner.email || "",
      phone: owner.phone || "",
    };
  };

  // Filter and sort owners
  const filterAndSortOwners = (
    owners: Owner[],
    filters: OwnerFilters
  ): Owner[] => {
    return owners
      .filter((owner) => {
        const searchLower = filters.searchTerm.toLowerCase();
        const matchesSearch = (value: string | null) =>
          value?.toLowerCase().includes(searchLower) || false;

        // Search in name and email
        return matchesSearch(owner.name) || matchesSearch(owner.email);
      })
      .sort((a, b) => {
        const aValue = (a[filters.sortBy as keyof Owner] || "").toString();
        const bValue = (b[filters.sortBy as keyof Owner] || "").toString();

        if (filters.sortBy === "created_at") {
          return new Date(bValue).getTime() - new Date(aValue).getTime();
        }

        return aValue.localeCompare(bValue);
      });
  };

  // Calculate statistics
  const statistics = {
    totalOwners: owners.length,
    totalProperties: owners.reduce(
      (sum, owner) => sum + (owner.property_count || 0),
      0
    ),
    averagePropertiesPerOwner:
      owners.length > 0
        ? owners.reduce((sum, owner) => sum + (owner.property_count || 0), 0) /
          owners.length
        : 0,
  };

  return {
    // Data
    owners,
    statistics,
    isAdmin,
    isLoading: isCheckingAdmin || isLoadingOwners,
    error: adminError || ownersError,

    // Actions
    createOwner: createOwnerMutation.mutate,
    updateOwner: updateOwnerMutation.mutate,
    sendVerification: sendVerificationMutation.mutate,
    refetch,

    // Loading states
    isCreating: createOwnerMutation.isPending,
    isUpdating: updateOwnerMutation.isPending,
    isSendingVerification: sendVerificationMutation.isPending,

    // Helper functions
    transformFormDataToOwnerData,
    transformOwnerToFormData,
    filterAndSortOwners,
  };
}
